import { type FormikValues } from "formik";

import { getObjectDiff, type ObjectDiff } from "utilities/object/object";

export interface IDirtyTrackingConfig<Values> {
    /**
     * These values will always show as "false" in the dirtyValues prop and will not affect isDirty.
     * @example [ "attachedFilesConfiguration", "checkedIds","lineItems.*.isExpanded" ]
     */
    ignoreDirtyValues?: ((keyof Values & string) | string)[];
    /**
     * This controls whether dirtyValues and isDirty are computed when form values change
     * @default true
     */
    enableDirtyTracking?: boolean;
}

type DirtyValues<Values extends FormikValues> = ObjectDiff<Values>;

export interface IDirtyTrackingInjectedProps<Values extends FormikValues> {
    getDirtyTrackingProps: () => IDirtyTrackingProps<Values>;
    /**
     * Sets the field value without triggering dirty tracking
     */
    setFieldValueWithoutDirtyTracking: (field: string, value: unknown) => void;
}

export interface IDirtyTrackingProps<Values extends FormikValues> {
    /**
     * Boolean values indicating if the properties of a form values object differ from their original state.
     * Return object follows the same structure as the source form values type.
     */
    dirtyValues: DirtyValues<Values>;
    /**
     * Whether any key on the form differs from its original state (excluding keys specified in ignoreDirtyValues).
     */
    isDirty: boolean;
    /**
     * A flat list of keys that are dirty, formatted like so: [ "title", "predecessor.0.lag", "description" ]
     */
    dirtyKeys: string[];
}

const disabledDirtyTrackingProps = { dirtyValues: {}, isDirty: false, dirtyKeys: [] };
const getDisabledDirtyTrackingProps = () => disabledDirtyTrackingProps;
const noopSetFieldValueWithoutDirtyTracking = () => {};

/**
 * Snapshots `getValues()` once and returns a `getDirtyTrackingProps` over that snapshot.
 *
 * The diff runs lazily on the first call and is cached for the lifetime of the returned
 * object, so repeat calls return the same result object without re-running `getObjectDiff`.
 * Call it inside `useMemo` keyed on the form's baseline and current values so the returned
 * function's identity changes exactly when the answer could.
 *
 * The cache keys on the identity of the form's values, so the form must never edit `values` or
 * `initialValues` (or anything reachable from them) in place: copy before writing and hand Formik
 * the copy (`setFieldValue`, `setValues`).
 *
 * Because the result is cached, callers must not mutate `dirtyValues` or `dirtyKeys`.
 */
export function getDirtyValues<Values extends FormikValues>(
    getValues: () => {
        initialValues: Values;
        currentValues: Values;
        setFieldValueWithoutDirtyTracking: (field: string, value: unknown) => void;
    },
    config: IDirtyTrackingConfig<Values>,
): IDirtyTrackingInjectedProps<Values> {
    if (config.enableDirtyTracking === false) {
        return {
            getDirtyTrackingProps: getDisabledDirtyTrackingProps,
            setFieldValueWithoutDirtyTracking: noopSetFieldValueWithoutDirtyTracking,
        };
    }
    const { initialValues, currentValues, setFieldValueWithoutDirtyTracking } = getValues();
    let cached: IDirtyTrackingProps<Values> | undefined;
    const getDirtyTrackingProps = () => {
        if (cached) {
            return cached;
        }
        const [diffObject, hasDiff, dirtyKeys] = getObjectDiff(
            initialValues,
            currentValues,
            config.ignoreDirtyValues,
        );
        cached = { dirtyValues: diffObject, isDirty: hasDiff, dirtyKeys };
        return cached;
    };
    return {
        getDirtyTrackingProps,
        setFieldValueWithoutDirtyTracking,
    };
}
